import type { History } from "history"
import type React from "react"
import { type PropsWithChildren, useCallback } from "react"
import { useNavigate } from "react-router-dom"
import styled, { css, useTheme } from "styled-components"
import type { CSSCursor, CSSTextAlign, CSSTextTransform } from "../types/css"
import type { BaseProps } from "../types/props"
import type { BorderProps } from "../types/props/border"
import { getBorderStyle } from "../util/props/border"
import { overloadColor } from "../util/style"
import { isUndefined } from "../util/typechecks"

interface WrapperProps {
  shadow?: string
  noShadow: boolean
  borderRadius: string
  cursor: CSSCursor
  background: string
  hoverColor?: string
  hoverBackground?: string
}
const Wrapper = styled.section<WrapperProps>`
  background: ${(props) => overloadColor(props.background)};
  border-radius: ${(props) => props.borderRadius};
  ${(props) =>
    !props.noShadow &&
    css`
      box-shadow: ${props.shadow ?? props.theme.layout.defaultShadow};
    `};

  overflow: hidden;
  cursor: ${(props) => props.cursor};

  &:hover {
    ${(props) =>
      props.hoverColor &&
      css`
        color: ${overloadColor(props.hoverColor)};
      `};
    ${(props) =>
      props.hoverBackground &&
      css`
        background: ${overloadColor(props.hoverBackground)};
      `};

    .--card-content {
      ${(props) =>
        props.hoverColor &&
        css`
          color: ${overloadColor(props.hoverColor)};
        `};
      ${(props) =>
        props.hoverBackground &&
        css`
          background: ${overloadColor(props.hoverBackground)};
        `};
    }
  }
`

const Header = styled.header<CardHeaderProps>`
  padding: ${(props) => props.padding};

  font-size: ${(props) => props.fontSize};
  text-align: ${(props) => props.textAlign};
  text-transform: ${(props) => props.textTransform};

  color: ${(props) => overloadColor(props.color)};
  background: ${(props) => overloadColor(props.background)};

  ${getBorderStyle}
`
const Content = styled.div<CardContentProps>`
  padding: ${(props) => props.padding};
  color: ${(props) => overloadColor(props.color)};
  background: ${(props) => overloadColor(props.background)};
  font-size: ${(props) => props.fontSize};
  text-align: ${(props) => props.textAlign};
  text-transform: ${(props) => props.textTransform};
`

interface CardHeaderProps extends BorderProps {
  padding: string
  fontSize: string
  textAlign: CSSTextAlign
  textTransform: CSSTextTransform
  color: string
  background: string
}
interface CardContentProps {
  padding: string
  fontSize: string
  textAlign: CSSTextAlign
  textTransform: CSSTextTransform
  color: string
  hoverColor?: string
  background: string
  hoverBackground?: string
}
export interface CardProps extends BaseProps, PropsWithChildren<object> {
  title?: string | React.ReactNode
  ref?: React.Ref<HTMLDivElement>

  shadow?: string
  noShadow?: boolean
  borderRadius?: string
  cursor?: CSSCursor
  href?: string
  hrefState?: History.LocationState | null

  headerProps?: Partial<CardHeaderProps>
  contentProps?: Partial<CardContentProps>

  onClick?: (event: React.MouseEvent<HTMLElement>) => void
}

export const Card: React.FC<CardProps> = ({
  className,
  children,
  ref,

  shadow,
  noShadow = false,
  borderRadius = "8px",
  cursor = "auto",
  href,
  hrefState,

  title,
  headerProps = {},
  contentProps = {},

  onClick = () => void 0,
}) => {
  const theme = useTheme()
  const navigate = useNavigate()

  const defaultedHeaderProps: CardHeaderProps = {
    padding: "8px 12px",
    textAlign: "left",
    textTransform: "none",
    fontSize: "1em",
    color: "text.dark",
    background: "veryLightBlue",
    border: `1px solid ${theme.colors.gray8}`,
    borderWidth: "0 0 1px 0",
    ...headerProps,
  }
  const defaultedContentProps: CardContentProps = {
    padding: "8px 12px",
    textAlign: "left",
    textTransform: "none",
    fontSize: "1em",
    color: "text.dark",
    background: "white",
    ...contentProps,
  }

  const handleClick = useCallback(
    (evt: React.MouseEvent<HTMLElement>) => {
      if (!isUndefined(href)) navigate(href, { state: hrefState ?? {} })

      onClick(evt)
    },
    [navigate, href, hrefState, onClick],
  )

  return (
    <Wrapper
      className={className}
      ref={ref}
      shadow={shadow}
      noShadow={noShadow}
      borderRadius={borderRadius}
      cursor={cursor}
      role={isUndefined(href) ? undefined : "link"}
      background={defaultedContentProps.background}
      hoverColor={defaultedContentProps.hoverColor}
      hoverBackground={defaultedContentProps.hoverBackground}
      onClick={handleClick}
    >
      {!isUndefined(title) && (
        <Header className="--card-header" {...defaultedHeaderProps}>
          {title}
        </Header>
      )}
      <Content className="--card-content" {...defaultedContentProps}>
        {children}
      </Content>
    </Wrapper>
  )
}
